import { Box, Text, Flex, Stack, Icon, Image, VStack } from '@chakra-ui/react';
import Logo from '@/assets/svg/logo.svg';
import CustomInput from '@/components/common/Input';
import CustomButton from '@/components/common/Button';
import keyIcon from '@/assets/svg/key.svg?react';
import mailIcon from '@/assets/svg/mail.svg?react';
import * as Yup from 'yup';
import arrowLeft from '@/assets/svg/backIcon.svg?react';
import { useNavigate } from 'react-router-dom';
import { NAVIGATION_ROUTES } from '@/routes/constants';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSendResetPasswordEmailMutation } from '@/services/forgotPassword/forgotPassword';
import { ISendResetEmail } from '@/services/auth/types';
import { Link as ReactRouterLink } from 'react-router-dom';
import OutlineButton from '@/components/common/UtilityButton';
import { AxiosError } from 'axios';
import AuthWrapper from '@/layouts/AuthWrapper';

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Please enter a valid email address.').required('Email is required.'),
});

const ForgotPassword = () => {
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitted },
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldFocusError: true,
  });

  const { mutateAsync: sendResetEmail, isLoading } = useSendResetPasswordEmailMutation();

  const onSubmit = async (data: ISendResetEmail) => {
    try {
      await sendResetEmail(data.email).then(() => {
        navigate(NAVIGATION_ROUTES.FORGOT_PASSWORD_LINK, {
          state: { email: data.email },
        });
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.data.email) {
          setError('email', {
            type: 'manual',
            message: error.response?.data.email[0],
          });
        }
      }
    }
  };

  return (
    <AuthWrapper>
      <Flex w="full" maxW="lg" p={8} bg="white" fontFamily={'primary'}>
        <Box
          position="absolute"
          top={{ base: '16px', lg: '24px' }}
          left={{ base: '16px', lg: '24px' }}
          p={'sm'}
        >
          <ReactRouterLink to="/">
            <Image
              src={Logo}
              alt="Logo"
              width={{ base: '115px', lg: '170px' }}
              height={'auto'}
              objectFit={'cover'}
            />
          </ReactRouterLink>
        </Box>

        <Flex
          width={{ base: '100%', lg: '500px' }}
          p={{ base: '16px', lg: 6 }}
          gap={8}
          borderRadius="md"
          flexDirection="column"
          justifyContent="center"
          bg="white"
          as={'form'}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Icon
            as={keyIcon}
            boxSize={12}
            p={3}
            borderRadius={'md'}
            color={'brand.primary.DEFAULT'}
            border={'1px solid'}
            borderColor={'border.default'}
          />
          <VStack direction={'row'} align={'stretch'} gap={1}>
            <Text
              color={'text.primary'}
              fontSize="4xl"
              fontFamily={'primary'}
              fontWeight={'semibold'}
            >
              Forgot Password?
            </Text>
            <Text fontSize="sm" color="text.secondary" fontFamily={'primary'}>
              No worries, we'll send you reset instructions.
            </Text>
          </VStack>

          <Stack spacing={6}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <CustomInput
                  {...field}
                  label="Email"
                  placeholder="Enter your email"
                  leftIcon={
                    <Icon as={mailIcon} color={errors.email ? 'status.error' : 'text.secondary'} />
                  }
                  error={isSubmitted ? errors.email : undefined}
                  onChange={(e) => {
                    field.onChange(e);
                    if (errors.email) clearErrors('email');
                  }}
                />
              )}
            />

            <CustomButton
              width="100%"
              fontSize="sm"
              fontWeight="semibold"
              borderRadius={'xl'}
              fontFamily={'primary'}
              type="submit"
              isLoading={isLoading}
            >
              Send Link
            </CustomButton>
          </Stack>
          <OutlineButton
            alignSelf={'center'}
            variant="noStyle"
            width="fit-content"
            fontSize="14px"
            fontWeight="semibold"
            fontFamily={'primary'}
            border={'none !important'}
            outline={'none'}
            icon={arrowLeft}
            text="Back to Sign in"
            color={'brand.primary.DEFAULT'}
            onClick={() => navigate(NAVIGATION_ROUTES.SIGNIN)}
            _hover={{ color: 'brand.primary.700', textDecoration: 'underline' }}
          ></OutlineButton>
        </Flex>

        {/* <Box position="absolute" bottom={'24px'} textAlign="center" width="100%">
          <Flex
            fontSize="xs"
            color="text.secondary"
            display={'flex'}
            justifyContent={'center'}
            gap={1}
            fontFamily={'primary'}
          >
            By creating an account, you agree to our{' '}
            <Text fontFamily={'primary'} textDecoration={'underline'} color="text.secondary">
              {' '}
              <ChakraLink href="https://kurtasari.com/terms-and-conditions" isExternal>
                terms of use
              </ChakraLink>
              .
            </Text>
          </Flex>
        </Box> */}
      </Flex>
    </AuthWrapper>
  );
};

export default ForgotPassword;
